import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useUser } from "@clerk/clerk-react";
import { getUserProgress, updateLessonProgress } from "../data/database";
import SaveButton from "../components/SaveButton";

const colors = [
  { word: "أَحْمَر", pronunciation: "aḥmar", meaning: "Red", color: "#FF0000" },
  { word: "أَزْرَق", pronunciation: "azraq", meaning: "Blue", color: "#0000FF" },
  { word: "أَخْضَر", pronunciation: "akhḍar", meaning: "Green", color: "#008000" },
  { word: "أَصْفَر", pronunciation: "aṣfar", meaning: "Yellow", color: "#FFFF00" },
  { word: "أَبْيَض", pronunciation: "abyaḍ", meaning: "White", color: "#FFFFFF" },
  { word: "أَسْوَد", pronunciation: "aswad", meaning: "Black", color: "#000000" },
  { word: "بُنِّي", pronunciation: "bunni", meaning: "Brown", color: "#8B4513" },
  { word: "بَنَفْسَجِي", pronunciation: "banafsaji", meaning: "Purple", color: "#800080" },
  { word: "وَرْدِي", pronunciation: "wardi", meaning: "Pink", color: "#FFC0CB" },
  { word: "رَمَادِي", pronunciation: "ramadi", meaning: "Gray", color: "#808080" }
];

function ColorsLesson() {
  const { user } = useUser();
  const [selectedColor, setSelectedColor] = useState(null);
  const [clickedColors, setClickedColors] = useState(new Set());

  // Load progress from database on mount
  useEffect(() => {
    const loadProgress = () => {
      if (user) {
        const userProgress = getUserProgress(user.id);
        const learnedCount = userProgress.colors || 0;
        if (learnedCount > 0) {
          setClickedColors(new Set(colors.slice(0, learnedCount).map(c => c.word)));
        }
      }
    };
    loadProgress();
  }, [user]);

  const handleClick = async (colorObj) => {
    setSelectedColor(colorObj);
    const updated = new Set(clickedColors).add(colorObj.word);
    setClickedColors(updated);
    
    // Save progress to database
    if (user) {
      try {
        await updateLessonProgress(user.id, 'colors', updated.size);
        console.log('Progress saved to database:', updated.size);
      } catch (error) {
        console.error('Error saving progress to database:', error);
      }
    }
    window.responsiveVoice.speak(colorObj.word, "Arabic Male");
  };

  const progress = (clickedColors.size / colors.length) * 100;

  return (
    <div dir="rtl" className="p-4 sm:p-6 text-center font-arabic h-full bg-gradient-to-br from-blue-50 to-white overflow-y-auto">
      <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-blue-800">الألوان العربية</h2>

      {/* Progress Bar */}
      <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
        <motion.div
          className="bg-blue-500 h-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <p className="text-sm text-gray-600 mb-6">تم تعلم {clickedColors.size} من {colors.length} لون</p>

      <AnimatePresence>
        {selectedColor && (
          <motion.div
            key="color-info"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="text-lg sm:text-xl text-blue-700 mb-6"
          >
            <p>النطق: <strong>{selectedColor.pronunciation}</strong></p>
            <p>المعنى: <strong>{selectedColor.meaning}</strong></p>
            <p>اللون: <span style={{ backgroundColor: selectedColor.color }} className="inline-block w-6 h-6 rounded-full"></span></p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center mb-6">
        {colors.map((colorObj, index) => {
          const learned = clickedColors.has(colorObj.word);
          return (
            <button
              key={index}
              onClick={() => handleClick(colorObj)}
              className={`text-xl font-semibold p-4 rounded-2xl shadow-md transition duration-200 text-wrap whitespace-pre-wrap break-words ${
                learned ? "bg-blue-100 text-white-700" : "bg-white text-blue-800 hover:bg-blue-100"
              }`}
              style={{ backgroundColor: learned ? colorObj.color : "white" }}
            >
              {colorObj.word}
            </button>
          );
        })}
      </div>

      {/* Save Button */}
      <div className="flex justify-center mb-6">
        <SaveButton
          lessonType="colors"
          progressData={clickedColors.size}
        />
      </div>

      {/* ResponsiveVoice Script */}
      <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
    </div>
  );
}

export default ColorsLesson;